import React, { useState, useMemo, useCallback, useEffect, lazy, Suspense } from 'react';
import { useRoute } from 'wouter';
import { Toaster, toast } from 'react-hot-toast';
import { useWouterRouter } from '../hooks/useWouterNavigation';
import { useAuth } from '../contexts/AuthContext';
import { useOrganizer } from '../contexts/OrganizerContext';
import { Event } from '../types/event.types';
import { Icon, StatCard, Button, SegmentedControl } from '../pages/DesignSystem';
import DashboardFilters, { DashboardFiltersState, defaultFilters } from '../pages/OrganizerEvents/components/DashboardFilters';
import type { AnalyticsPeriod } from '../services/OrganizerAnalyticsService';
import { IconName } from '../pages/DesignSystem/primitives/Icon';

import OrganizerHomeView from '../components/organizer/OrganizerHomeView/OrganizerHomeView';
import { EventFormModal } from '../components/organizer/EventFormModal';
import { ProfileEditModal } from '../components/organizer/ProfileEditModal';
import { SubscriptionModal } from '../components/organizer/SubscriptionModal';
import { OnboardingModal } from '../components/organizer/OnboardingModal';
import { ProductTour } from '../components/organizer/ProductTour';
import { OrganizerProfilePrivatePage } from '../pages/OrganizerProfilePrivate';
import { useOnboarding } from '../hooks/useOnboarding';
import { useProductTour } from '../hooks/useProductTour';
import { ViewToggle, PlanningView, type ViewMode } from '../components/organizer/Planning';

import '../components/organizer/EventImport/EventImport.css';
import './styles/layout-variables.css';
import './styles/organizer-desktop-layout.css';

// Lazy load ImportModal (includes SheetJS ~400KB)
const ImportModal = lazy(() => import('../components/organizer/EventImport/ImportModal'));

// Menu item interface
interface MenuItem {
  id: 'home' | 'profile' | 'create' | 'external';
  icon: IconName;
  label: string;
  path?: string;
  onClick?: () => void;
  badge?: string;
  isExternal?: boolean;
  dividerBefore?: boolean;
}

// Format number helper
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

// Period options for segmented control (ordered from shortest to longest)
const periodOptions: { value: AnalyticsPeriod; label: string }[] = [
  { value: '7days', label: '7 days' },
  { value: '1month', label: '1 month' },
  { value: 'alltime', label: 'All time' }
];

// Storytelling variants (12 total) — one is selected randomly at mount
// Note: n = event views (impressions), NOT unique tourists
const STORYTELLING_VARIANTS: ((n: number) => string)[] = [
  (n) => `your events were seen **${n} times** — and probably even more eyes!`,
  (n) => `**${n} views** on your events — that's real impact!`,
  (n) => `your events were seen **${n} times** — every view is a potential guest walking through your door!`,
  (n) => `your events popped up **${n} times** — that's a lot of eyes discovering what you do!`,
  (n) => `**${n} views** on your events — keep it up, it's working!`,
  (n) => `your events caught the eye **${n} times** — you're on fire!`,
  (n) => `your events were seen **${n} times** — that's **${n}** chances to make someone's trip unforgettable!`,
  (n) => `**${n} views** on your events — word is getting around!`,
  (n) => `your events were seen **${n} times** — imagine how many told their friends!`,
  (n) => `your events popped up **${n} times** — each view could be your next guest!`,
  (n) => `**${n} views** on your events — and that's just the beginning!`,
  (n) => `your events were seen **${n} times** — your community is growing!`,
];

const PERIOD_PREFIXES: Record<AnalyticsPeriod, string> = {
  '7days': 'In the last 7 days,',
  '1month': 'This month,',
  'alltime': 'Since you joined,',
};

const getStorytellingPhrase = (
  impressions: number,
  period: AnalyticsPeriod,
  variantIndex: number
): string | null => {
  if (impressions === 0) return null;
  if (impressions < 30) return "Your events are live! Tourists are starting to discover them.";
  const prefix = PERIOD_PREFIXES[period];
  const variant = STORYTELLING_VARIANTS[variantIndex % STORYTELLING_VARIANTS.length];
  return `${prefix} ${variant(impressions)}`;
};

const renderBoldText = (text: string): React.ReactNode => {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );
};

const getActiveEventsCount = (
  events: { datetime?: Date; endDateTime?: string; status: string; seriesId?: string }[],
  period: AnalyticsPeriod
): number => {
  const publishedEvents = events.filter(e => e.status === 'active' || e.status === 'cancelled');

  let filtered = publishedEvents;
  if (period !== 'alltime') {
    const days = period === '7days' ? 7 : 30;
    const periodStart = new Date();
    periodStart.setDate(periodStart.getDate() - days);
    filtered = publishedEvents.filter(e => {
      const endDate = e.endDateTime ? new Date(e.endDateTime) : e.datetime;
      if (!endDate) return false;
      return endDate >= periodStart;
    });
  }

  // Group by seriesId: a recurring event with N occurrences counts as 1
  const seen = new Set<string>();
  let count = 0;
  for (const e of filtered) {
    if (e.seriesId) {
      if (!seen.has(e.seriesId)) {
        seen.add(e.seriesId);
        count++;
      }
    } else {
      count++;
    }
  }
  return count;
};

// 🔧 FIX: Tracking pour history.back() au lieu de push quand on revient au dashboard
let _orgDesktopOnSubPage = false;

const OrganizerDesktopLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const { navigate, location } = useWouterRouter();
  const { organizerProfile, organizerEvents, eventsLoading, dashboardData, analyticsLoading, eventCounts, analyticsPeriod, setAnalyticsPeriod, updateProfile } = useOrganizer();

  // Onboarding state (PRD-onboarding-organisateur.md)
  const {
    needsOnboarding,
    currentStep,
    nextStep,
    prevStep,
    completeOnboarding,
    error: onboardingError,
  } = useOnboarding(organizerProfile, user?.uid || null);

  // 🔧 FIX (item 2 - PRD robustesse réseau faible): l'état d'erreur d'onboarding
  // était calculé par le hook mais jamais récupéré/affiché ici. On le surface
  // désormais en toast pour que l'utilisateur voie toujours quand ça échoue.
  useEffect(() => {
    if (onboardingError) {
      toast.error(onboardingError);
    }
  }, [onboardingError]);

  // Product Tour state (PRD-product-tour-organisateur.md)
  const {
    isRunning: isTourRunning,
    completeTour,
    skipTour,
    replayTour,
  } = useProductTour();

  // Route detection
  const [matchProfile] = useRoute('/organizer/profile');
  const [matchEventId, params] = useRoute('/organizer/:id');

  // Event selection state
  const [selectedEvent, setSelectedEvent] = useState<Event | undefined>(undefined);
  const [isEditingOccurrence, setIsEditingOccurrence] = useState(false);

  // Panel states
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [isProfileEditModalOpen, setIsProfileEditModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  // Tabs state
  type ActiveTab = 'active' | 'draft' | 'past';
  const [activeTab, setActiveTab] = useState<ActiveTab>('active');

  // Filters state (PRD-dashboard-filters)
  const [filters, setFilters] = useState<DashboardFiltersState>(defaultFilters);

  // Planning view mode (PRD-planning-view)
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('w2d_planning_viewMode');
    return (saved === 'planning' ? 'planning' : 'list') as ViewMode;
  });

  // Persist viewMode to localStorage
  useEffect(() => {
    localStorage.setItem('w2d_planning_viewMode', viewMode);
  }, [viewMode]);

  // 🔧 FIX: Guard supprimé - il créait des entrées fantômes (pushState à chaque mount)
  // Le comportement naturel est correct : back depuis dashboard → retour au public (ou quitte l'app)

  // Filtered events (PRD-dashboard-filters)
  const filteredEvents = useMemo(() => {
    if (!organizerEvents) return [];

    return organizerEvents.filter(event => {
      // Text search filter
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        if (!event.title?.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Start date filter
      if (filters.dateRange.start && event.datetime) {
        const eventDate = new Date(event.datetime);
        const startOfDay = new Date(filters.dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (eventDate < startOfDay) {
          return false;
        }
      }

      // End date filter
      if (filters.dateRange.end && event.datetime) {
        const eventDate = new Date(event.datetime);
        const endOfDay = new Date(filters.dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (eventDate > endOfDay) {
          return false;
        }
      }

      // Cancelled events filter (only for Active tab)
      if (activeTab === 'active' && !filters.showCancelled) {
        if ((event as any).status === 'cancelled') {
          return false;
        }
      }

      return true;
    });
  }, [organizerEvents, filters, activeTab]);

  // KPIs data
  const kpis = dashboardData?.kpis || {
    eventsPublies: eventCounts.active,
    connectionsZone: null,
    apparitionsEvents: 0,
    clicsEvents: 0,
    likesEvents: 0,
    hasZone: false
  };

  // Storytelling: random variant index, stable across re-renders
  const variantIndex = useMemo(() => Math.floor(Math.random() * STORYTELLING_VARIANTS.length), []);

  // Active events count for the selected period
  const activeEventsOnPeriod = useMemo(
    () => getActiveEventsCount(organizerEvents, analyticsPeriod),
    [organizerEvents, analyticsPeriod]
  );

  // Per-event average
  const avgPerEvent = activeEventsOnPeriod > 0
    ? Math.ceil(kpis.apparitionsEvents / activeEventsOnPeriod)
    : 0;

  const perEventDescription = activeEventsOnPeriod === 1
    ? 'views on your event'
    : `views per event (across ${activeEventsOnPeriod} events)`;

  // Storytelling phrase
  const storytellingPhrase = getStorytellingPhrase(
    kpis.apparitionsEvents,
    analyticsPeriod,
    variantIndex
  );

  // Handle create event button click
  const handleCreateEvent = useCallback(() => {
    setSelectedEvent(undefined);
    setIsEditingOccurrence(false);
    setIsFormModalOpen(true);
  }, []);

  // Handle create event at a specific date/time (from planning grid click)
  const handleCreateEventAt = useCallback((datetime: Date) => {
    // Create a partial event with just the datetime pre-filled
    setSelectedEvent({ datetime } as Event);
    setIsEditingOccurrence(false);
    setIsFormModalOpen(true);
  }, []);

  const handleOpenImport = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  const handleCloseImport = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const handleGoToDrafts = useCallback(() => {
    setIsImportModalOpen(false);
    setActiveTab('draft');
    setViewMode('list');
  }, []);

  // Switch from planning view to list view with Drafts tab (PRD-planning-view)
  const handleSwitchToDraftsList = useCallback(() => {
    setViewMode('list');
    setActiveTab('draft');
  }, []);

  // Menu items (Email & WhatsApp moved to Help menu)
  const menuItems: MenuItem[] = useMemo(() => [
    { id: 'home', icon: 'LayoutDashboard', label: 'Dashboard', path: '/organizer' },
    { id: 'profile', icon: 'User', label: 'Profile', path: '/organizer/profile', badge: !organizerProfile ? '!' : undefined },
    { id: 'create', icon: 'Plus', label: 'Create Event', onClick: handleCreateEvent },
    { id: 'external', icon: 'Compass', label: 'Public Space', path: '/events', dividerBefore: true }
  ], [organizerProfile, handleCreateEvent]);

  // Determine active nav item
  const getActiveNavItem = (): 'home' | 'profile' => {
    if (matchProfile) return 'profile';
    return 'home';
  };

  // Handle event selection from list - opens form modal
  const handleEventSelect = (event: Event, modifyType?: 'single' | 'series' | 'occurrence') => {
    setSelectedEvent(event);
    setIsEditingOccurrence(modifyType === 'occurrence');
    setIsFormModalOpen(true);
    // ✅ Navigation intelligente : replace pour éviter accumulation d'events dans l'historique
    navigate(`/organizer/${event.id}`, { replace: true });
  };

  // Close form modal
  const closeFormModal = () => {
    setIsFormModalOpen(false);
    setSelectedEvent(undefined);
    setIsEditingOccurrence(false);
    // 🔧 FIX: replace au lieu de push pour ne pas accumuler d'historique
    navigate('/organizer', { replace: true });
  };

  // After successful submission
  const handleFormSuccess = () => {
    closeFormModal();
  };

  // Duplicate event
  const handleDuplicate = (duplicatedEvent: Partial<Event>) => {
    setSelectedEvent(duplicatedEvent as Event);
    setIsEditingOccurrence(false);
    setIsFormModalOpen(true);
    // 🔧 FIX: replace au lieu de push
    navigate('/organizer', { replace: true });
  };

  // Navigation handler
  // 🔧 FIX: Utilise history.back() pour revenir au dashboard au lieu de push
  const handleNavigate = (path: string) => {
    if (path !== location.pathname) {
      setSelectedEvent(undefined);
      setIsEditingOccurrence(false);

      const currentPath = location.pathname;
      const isOnDashboard = currentPath === '/organizer' || currentPath === '/organizer/';
      const goingToDashboard = path === '/organizer' || path === '/organizer/';

      if (goingToDashboard && _orgDesktopOnSubPage) {
        // Retour au dashboard : back() au lieu de push → pas d'entrée fantôme
        _orgDesktopOnSubPage = false;
        window.history.back();
      } else if (!goingToDashboard && isOnDashboard && path.startsWith('/organizer/')) {
        // Dashboard → sous-page organizer : track et push
        _orgDesktopOnSubPage = true;
        navigate(path);
      } else {
        // Autre navigation (Public Space, etc.)
        _orgDesktopOnSubPage = false;
        navigate(path);
      }
    }
    setIsFormModalOpen(false);
  };

  // Handle menu item click
  const handleMenuItemClick = (item: MenuItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.path) {
      handleNavigate(item.path);
    }
  };

  // Handle logout - always show confirmation
  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  // Handle help menu
  const handleHelpClick = () => {
    setShowHelpMenu(!showHelpMenu);
  };

  const closeHelpMenu = () => {
    setShowHelpMenu(false);
  };

  const performLogout = async () => {
    try {
      setShowLogoutConfirm(false);
      await logout();
      handleNavigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Profile modal handlers
  const handleEditProfileClick = () => {
    setIsProfileEditModalOpen(true);
  };

  const handleManageSubscriptionClick = () => {
    setIsSubscriptionModalOpen(true);
  };

  // Profile page or home view
  const isProfilePage = matchProfile;

  // Selected event ID from URL
  const selectedEventId = matchEventId && params?.id !== 'profile' ? params?.id : undefined;

  return (
    <div className="organizer-desktop-layout">
      {/* Left Sidebar - Hover to expand */}
      <aside className="organizer-desktop-layout__sidebar">
        {/* Sidebar Header - Logo */}
        <div className="organizer-desktop-layout__sidebar-header">
          <div className="organizer-desktop-layout__logo">
            <img src="/images/logoverttexte.webp" alt="What 2 Do !" />
          </div>
        </div>

        {/* Sidebar Navigation */}
        <nav className="organizer-desktop-layout__sidebar-nav">
          {menuItems.map((item, index) => (
            <React.Fragment key={`${item.id}-${index}`}>
              {item.dividerBefore && <div className="organizer-desktop-layout__sidebar-divider" />}
              <button
                className={`organizer-desktop-layout__sidebar-item ${item.id !== 'external' && item.id !== 'create' && getActiveNavItem() === item.id ? 'organizer-desktop-layout__sidebar-item--active' : ''}`}
                onClick={() => handleMenuItemClick(item)}
                type="button"
                title={item.label}
              >
                <span className="organizer-desktop-layout__sidebar-item-icon">
                  <Icon name={item.icon} size={20} />
                </span>
                <span className="organizer-desktop-layout__sidebar-item-label">{item.label}</span>
                {item.badge && <span className="organizer-desktop-layout__sidebar-item-badge">{item.badge}</span>}
                {item.isExternal && <Icon name="ExternalLink" size={14} className="organizer-desktop-layout__sidebar-item-external" />}
              </button>
            </React.Fragment>
          ))}
        </nav>

        {/* Sidebar Footer - Help, Feedback, Legal & Logout */}
        <div className="organizer-desktop-layout__sidebar-footer">
          <div className="organizer-desktop-layout__sidebar-divider" />
          <button
            className="organizer-desktop-layout__sidebar-item"
            onClick={handleHelpClick}
            type="button"
            title="Help"
          >
            <span className="organizer-desktop-layout__sidebar-item-icon">
              <Icon name="CircleHelp" size={20} />
            </span>
            <span className="organizer-desktop-layout__sidebar-item-label">Help</span>
          </button>
          <button
            className="organizer-desktop-layout__sidebar-item"
            onClick={() => window.open('https://forms.gle/WsLPhinC1fS5eYJcA', '_blank')}
            type="button"
            title="Feedback"
          >
            <span className="organizer-desktop-layout__sidebar-item-icon">
              <Icon name="MessageSquare" size={20} />
            </span>
            <span className="organizer-desktop-layout__sidebar-item-label">Feedback</span>
            <Icon name="ExternalLink" size={14} className="organizer-desktop-layout__sidebar-item-external" />
          </button>
          <button
            className="organizer-desktop-layout__sidebar-item"
            onClick={() => navigate('/about')}
            type="button"
            title="About"
          >
            <span className="organizer-desktop-layout__sidebar-item-icon">
              <Icon name="Info" size={20} />
            </span>
            <span className="organizer-desktop-layout__sidebar-item-label">About</span>
          </button>
          <button
            className="organizer-desktop-layout__sidebar-item"
            onClick={() => navigate('/legal')}
            type="button"
            title="Legal & Privacy"
          >
            <span className="organizer-desktop-layout__sidebar-item-icon">
              <Icon name="Shield" size={20} />
            </span>
            <span className="organizer-desktop-layout__sidebar-item-label">Legal & Privacy</span>
          </button>
          <button
            className="organizer-desktop-layout__sidebar-item organizer-desktop-layout__sidebar-item--logout"
            onClick={handleLogoutClick}
            type="button"
            title="Logout"
          >
            <span className="organizer-desktop-layout__sidebar-item-icon">
              <Icon name="LogOut" size={20} />
            </span>
            <span className="organizer-desktop-layout__sidebar-item-label">Logout</span>
          </button>
        </div>

      </aside>

      {/* Help Menu Popup */}
      {showHelpMenu && (
        <>
          {/* Backdrop to close on outside click */}
          <div
            className="organizer-desktop-layout__help-backdrop"
            onClick={closeHelpMenu}
          />
          <div className="organizer-desktop-layout__help-menu">
            <h3 className="organizer-desktop-layout__help-menu-title">How can we help?</h3>

            <button
              className="organizer-desktop-layout__help-menu-item"
              onClick={() => { closeHelpMenu(); replayTour(); }}
              type="button"
            >
              <Icon name="Navigation" size={20} />
              <div className="organizer-desktop-layout__help-menu-item-content">
                <span>Take a Tour</span>
                <p>Discover all the features of your dashboard</p>
              </div>
            </button>

            <button
              className="organizer-desktop-layout__help-menu-item organizer-desktop-layout__help-menu-item--disabled"
              type="button"
              disabled
            >
              <Icon name="BookOpen" size={20} />
              <div className="organizer-desktop-layout__help-menu-item-content">
                <span>How it works <em>(Coming soon)</em></span>
                <p>Learn how to get the most out of What 2 Do</p>
              </div>
            </button>

            <div className="organizer-desktop-layout__help-menu-divider" />

            <div className="organizer-desktop-layout__help-menu-contact">
              <p>Need help? Reach out to us!</p>
              <div className="organizer-desktop-layout__help-menu-contact-buttons">
                <button
                  className="organizer-desktop-layout__help-menu-contact-btn organizer-desktop-layout__help-menu-contact-btn--whatsapp"
                  onClick={() => { window.open('https://wa.me/33771006946', '_blank'); closeHelpMenu(); }}
                  type="button"
                >
                  <Icon name="MessageCircle" size={18} />
                  WhatsApp
                </button>
                <button
                  className="organizer-desktop-layout__help-menu-contact-btn organizer-desktop-layout__help-menu-contact-btn--email"
                  onClick={() => { window.location.href = 'mailto:nico@what2do.today'; closeHelpMenu(); }}
                  type="button"
                >
                  <Icon name="Mail" size={18} />
                  Email
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Logout Confirmation Popup - Outside sidebar to avoid overflow issues */}
      {showLogoutConfirm && (
        <div className="organizer-desktop-layout__logout-confirm">
          <p>Are you sure you want to log out?</p>
          <div className="organizer-desktop-layout__logout-confirm-actions">
            <Button variant="ghost" size="sm" onClick={() => setShowLogoutConfirm(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={performLogout}>
              Logout
            </Button>
          </div>
        </div>
      )}

      {/* Center Content Zone */}
      <main className="organizer-desktop-layout__content">
        {/* Header - Fixed at top, doesn't scroll */}
        {!isProfilePage && (
          <div className="organizer-desktop-layout__sticky-header">
            <div className="organizer-desktop-layout__header-top">
              <div className="organizer-desktop-layout__title-row">
                <h1 className="organizer-desktop-layout__title">My Events</h1>
                <ViewToggle value={viewMode} onChange={setViewMode} />
              </div>
              <div className="organizer-desktop-layout__header-actions">
                <Button variant="secondary" size="md" onClick={handleOpenImport} className="organizer-desktop-layout__import-button">
                  <Icon name="Upload" size={18} />
                  Import
                </Button>
                <Button variant="primary" size="md" onClick={handleCreateEvent} className="organizer-desktop-layout__create-button">
                  <Icon name="Plus" size={18} />
                  Create an Event
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Scrollable area - KPIs + tabs + content all scroll together */}
        <div className="organizer-desktop-layout__scrollable">
          {/* KPIs — scrollable, disappear on scroll (both modes) */}
          {!isProfilePage && (
            <div className="organizer-desktop-layout__kpis-section">
              <div className="organizer-desktop-layout__kpis-header">
                <SegmentedControl
                  options={periodOptions}
                  value={analyticsPeriod}
                  onChange={setAnalyticsPeriod}
                  disabled={analyticsLoading}
                  size="sm"
                  variant="neutral"
                />
              </div>
              <div className="organizer-desktop-layout__kpis-row">
                <StatCard label="Tourists nearby" value={analyticsLoading ? 0 : (kpis.connectionsZone !== null ? formatNumber(kpis.connectionsZone) : '—')} icon="Users" color="blue" variant="glass" loading={analyticsLoading} description={!kpis.hasZone ? 'Set your address in profile' : 'tourists connected around you'} />
                <StatCard label="Event views" value={analyticsLoading ? 0 : formatNumber(kpis.apparitionsEvents)} icon="Eye" color="purple" variant="glass" loading={analyticsLoading} description="times your events were seen" />
                {activeEventsOnPeriod > 0 && (
                  <StatCard label="Per event" value={analyticsLoading ? 0 : formatNumber(avgPerEvent)} icon="BarChart3" color="green" variant="glass" loading={analyticsLoading} description={perEventDescription} />
                )}
              </div>
              {storytellingPhrase && !analyticsLoading && (
                <div className="organizer-desktop-layout__storytelling">
                  <p className="organizer-desktop-layout__storytelling-text">
                    {renderBoldText(storytellingPhrase)}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Tabs — below KPIs, hidden in planning mode */}
          {!isProfilePage && viewMode !== 'planning' && (
            <div className="organizer-desktop-layout__tabs">
              {(['active', 'draft', 'past'] as const).map((tab) => (
                <button
                  key={tab}
                  className={`organizer-desktop-layout__tab ${activeTab === tab ? 'organizer-desktop-layout__tab--active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                  type="button"
                >
                  {tab === 'active' && `Active (${eventCounts.active})`}
                  {tab === 'draft' && `Drafts (${eventCounts.draft})`}
                  {tab === 'past' && `Past (${eventCounts.past})`}
                </button>
              ))}
            </div>
          )}

          {/* Dashboard Filters — only in list mode (PRD-dashboard-filters) */}
          {!isProfilePage && viewMode !== 'planning' && (
            <DashboardFilters
              filters={filters}
              onFiltersChange={setFilters}
              activeTab={activeTab}
            />
          )}

          {/* Content */}
          {isProfilePage ? (
            <OrganizerProfilePrivatePage
              onEditClick={handleEditProfileClick}
              onManageSubscriptionClick={handleManageSubscriptionClick}
            />
          ) : viewMode === 'planning' ? (
            <PlanningView
              events={organizerEvents}
              eventsLoading={eventsLoading}
              onEventSelect={handleEventSelect}
              onDuplicate={handleDuplicate}
              onCreateEvent={handleCreateEvent}
              onCreateEventAt={handleCreateEventAt}
              onSwitchToDrafts={handleSwitchToDraftsList}
              variant="desktop"
            />
          ) : (
            <OrganizerHomeView
              onEventSelect={handleEventSelect}
              onDuplicate={handleDuplicate}
              selectedEventId={selectedEventId}
              hideHeader={true}
              hideTabs={true}
              activeTab={activeTab}
              onCreateEvent={handleCreateEvent}
              filteredEvents={filteredEvents}
            />
          )}
        </div>
      </main>

      {/* Event Form Modal */}
      <EventFormModal
        isOpen={isFormModalOpen}
        event={selectedEvent}
        organizerId={user?.uid || ''}
        onClose={closeFormModal}
        onSubmitSuccess={handleFormSuccess}
        onDuplicate={handleDuplicate}
        isEditingOccurrence={isEditingOccurrence}
      />

      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={isProfileEditModalOpen}
        onClose={() => setIsProfileEditModalOpen(false)}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        organizerId={user?.uid || ''}
      />

      {/* Import Modal - lazy loaded (PRD-import-intelligent-events.md) */}
      {isImportModalOpen && (
        <Suspense fallback={null}>
          <ImportModal
            isOpen={isImportModalOpen}
            onClose={handleCloseImport}
            onGoToDrafts={handleGoToDrafts}
          />
        </Suspense>
      )}

      {/* Onboarding Modal - PRD-onboarding-organisateur.md */}
      <OnboardingModal
        isOpen={needsOnboarding}
        organizerProfile={organizerProfile}
        userId={user?.uid || ''}
        currentStep={currentStep}
        onNextStep={nextStep}
        onPrevStep={prevStep}
        onComplete={completeOnboarding}
        onProfileUpdate={updateProfile}
      />

      {/* Product Tour - PRD-product-tour-organisateur.md */}
      <ProductTour
        isRunning={isTourRunning && !needsOnboarding}
        onComplete={completeTour}
        onSkip={skipTour}
      />

      <Toaster position="top-right" toastOptions={{ className: 'organizer-toast', duration: 3000 }} />
    </div>
  );
};

export default OrganizerDesktopLayout;
